import type { SealState } from './types';
import { civilFromEpochDay, dayOfYear, epochDayFromCivil } from './calendar';
import { YEAR_DAYS, dailyDeath, type PopulationDraws } from './population';
import { MONK_SEAL_ADULT_PUPS } from './data/seals/monkseal';

/**
 * The Hawaiian monk seals of Maui Nui (M103): Maui Nui's share of the main
 * Hawaiian Islands' seals, which NOAA counts as one subpopulation ("By
 * convention, the main Hawaiian Islands are treated as a single subpopulation
 * because the islands are relatively close to one another and seals readily
 * move among them", Baker et al. 2016, p. 320), counted by year class since
 * birth, with the pups still nursing apart, day by day since their birth.
 * The day's step ages them on the first of January; pups are born to the
 * females from their first pupping, evenly through NOAA's pupping season;
 * a pup nurses its 44 days and is weaned into its year's class; and every
 * seal dies at its age's rate. Its draws are the caller's, as the honu's and
 * the humpbacks' are: binomial deaths and Poisson pups on its own stream in
 * the game, expected values in the table (`scripts/monkseal.ts`).
 *
 * **Its survival is NOAA's, measured by age, and its one fitted rate the
 * adult females' pups a year** (`MONK_SEAL_ADULT_PUPS`), set so the seals
 * grow as NOAA's own life table for the main islands grows them, 1.050 a
 * year ("Intrinsic", by "Leslie matrices"), where its counts from 2013 to
 * 2024 give 1.048 ("Median realized population growth rates"; PIFSC 2025,
 * Tables 4 and 3). The pups by the mother's age and the births through the
 * year were read off Robinson et al. 2021's charts until M119, two of its
 * figures read whole (102 values), which the game does not carry from a
 * paper (`CLAUDE.md`; the paper is CC BY-NC-ND), and no source granting use
 * gives either (M119's reader), so both are kept simple: a
 * pupping age and one rate, and NOAA's pupping season. The first population
 * in the game with its own trend; no crowding is coded (none measured), so
 * it grows without limit (open).
 */

/** A seal's life, each figure with its source. */
export interface SealLife {
  id: 'monk';
  /** The main islands' seals and the year counted, and Maui Nui's share of them. */
  mainIslands: number;
  censusYear: number;
  mauiNuiPups: number;
  mainIslandPups: number;
  /** Survival: birth to weaning over the days a pup nurses, weaning to one, then a year at each age (`survivalOf`). */
  weaningSurvival: number;
  nursingDays: number;
  weanedSurvival: number;
  /** A year's survival at 1, 2 and 3; adults' from 4 to `adultUntilYears`; then the old's. */
  youngSurvival: readonly number[];
  adultSurvival: number;
  adultUntilYears: number;
  oldSurvival: number;
  /** The age females begin pupping, years: half of them at it, all from the year after; none before. */
  firstPupYears: number;
  /** Pups a year to each female from the year after `firstPupYears`, and the share of seals female. */
  adultPups: number;
  femaleShare: number;
  /** The oldest class, years: its seals die at the year's turn. */
  oldestYears: number;
  /** The pupping season, its first and last days as [month, day]: every pup born in it, evenly. */
  pupping: { from: readonly [number, number]; to: readonly [number, number] };
}

/**
 * The share of Maui Nui's seals' dives below 30 m, the Reef's floor's depth (M203; M145's, `SEAL_VISIT_ODDS`): "Most
 * dives occurred in shallow water (<40 m) (median = 12 m, IQR = 4.4–32 m)" (Wilson, Littnan and Read 2017, *Marine
 * Mammal Science* 33, accepted manuscript p. 10; 397,496 dives of 19 seals tagged in the main islands, MEASURED): a
 * quarter below their upper quartile's 32 m, taken as a quarter below 30 m (a reading). "Monk seals are benthic
 * foragers and consequently, most dives were to the sea floor" (p. 10), "most seals remained within the 200 m isobath
 * and foraged along the island coastlines" (p. 11): the rest on the Slope's floor.
 */
export const SEAL_DIVES_BELOW_30M = 0.25;

/**
 * The Hawaiian monk seal, *Neomonachus schauinslandi* (‘ilio holo i ka uaua,
 * "dog that runs in rough waters", Costa and Kendall 2016, p. 266), of Maui
 * Nui, each figure with its source as read (M103). PIFSC 2025 is NOAA's
 * *Hawaiian Monk Seal Population Summary 2024* (PIFSC Data Report DR-25-03),
 * which calls itself provisional; its figures are read from its charts by
 * their pixels where it prints none.
 */
export const MONK_SEAL: SealLife = {
  id: 'monk',
  /**
   * "Subtotal MHI 338 288 387 55 393 343 442" (PIFSC 2025, Table 2: non-pups'
   * median and limits, pups, all seals' median and limits), the main islands'
   * seals in 2024: Kauaʻi to Hawaiʻi Island "the minimum tally of all
   * individuals identified by an established sighting network during the
   * calendar year", Niʻihau and Lehua corrected counts (Carretta et al. 2026,
   * p. 10), the year's seals taken as its last day's (a reading: a tally
   * through the year holds the seals that died in it, but a minimum misses
   * some; the two partly offset).
   */
  mainIslands: 393,
  censusYear: 2024,
  /**
   * Maui Nui's share by where its pups are born: Molokaʻi's, Lānaʻi's, Maui's
   * and Kahoʻolawe's pups of the main islands' from 2013 ("Since 2013, a
   * small number of surveys of privately owned Ni'ihau Island and nearby
   * Lehua Island have been conducted", Baker et al. 2016, p. 320) to 2024,
   * 136 of 486 (28.0%), MEASURED from
   * PIFSC 2025's Fig. 13 ("Number of pups counted in the MHI; colors indicate
   * birth islands") by its pixels: each bar's colours in pups by the axis's
   * ticks, every reading within 0.03 of a whole pup, the years' totals NOAA's
   * own (55 in 2024, "A record high 55 pups"; 2022's 17 at Niʻihau and 25 at
   * the rest, Carretta et al. 2026, Table 2). The seals' share taken as their
   * pups' (a reading: 63% of seals tagged stayed by their island, Wilson et
   * al. 2017; Niʻihau's 21 of 2024's 55 pups and 155 of its 393 seals agree).
   */
  mauiNuiPups: 136,
  mainIslandPups: 486,
  /**
   * The main islands' pups from birth to weaning, all years, 0.880 (95%
   * interval 0.843 to 0.909), MEASURED from PIFSC 2025's Fig. 15 by its
   * pixels ("MHIKH", Kauaʻi to Hawaiʻi Island). Nursing 44 days: "Nursing
   * lasts, on average, 39 days (Johanos et al. 1994)" (NMFS 2014, the
   * Hawaiian monk seal recovery actions' programmatic environmental impact
   * statement, p. 3-13, public domain), and "Mothers in the main Hawaiian
   * Islands nurse their pups for about 5 days longer ... than females in the
   * Northwestern Hawaiian Islands" (NOAA Fisheries, "Citizen Scientists Help
   * Reveal Undetected Hawaiian Monk Seal Reproduction", 23 April 2021, a US
   * government work): 39 and 5 (M119; until then Robinson et al. 2021's
   * mean, 43.7 days, a single figure the game could cite (M123), kept as a check).
   */
  weaningSurvival: 0.88,
  nursingDays: 44,
  /**
   * PIFSC 2025's Fig. 23, "Age-specific post-weaning survival estimates in the
   * main Hawaiian Islands. Best fit model with no time variation", MEASURED by
   * its pixels (each point's centre on the axis's ticks, 0.00044 a pixel):
   * weaning to 1 year 0.897, 1 to 2 0.818, 2 to 3 0.903, 3 to 4 0.895,
   * "adult (4 to 17 yr)" 0.924, "senescent (18+ yr)" 0.933.
   */
  weanedSurvival: 0.897,
  youngSurvival: [0.818, 0.903, 0.895],
  adultSurvival: 0.924,
  adultUntilYears: 18,
  oldSurvival: 0.933,
  /**
   * "The youngest documented female to give birth was 4 years old. Typically
   * females begin reproducing at ages 5 to 6 in the main Hawaiian Islands"
   * (NOAA Fisheries' species page, read 19 October 2026): half begin at 5,
   * the rest at 6 (a reading of "5 to 6"), none before.
   */
  firstPupYears: 5,
  /**
   * Fitted (`npm run monkseal`): the pups a year to each female from 6 that
   * grow the seals, at NOAA's survival above and half the pups female, as
   * NOAA's life table for the main islands does, 1.050 a year (PIFSC 2025,
   * Table 4, "Intrinsic"); kept the same to every age, as no source the game
   * may carry gives them by age ("Female Hawaiian monk seals can pup into
   * their mid- to late-20s, possibly even older!", NOAA Fisheries'
   * frequently asked questions on monk seal mothers and pups). A check, not
   * carried: Robinson et al. 2021's peak, 0.79 at 10 (its Table 1 and
   * Fig. 6).
   */
  adultPups: MONK_SEAL_ADULT_PUPS,
  /** Assumed: half the pups female, no source read giving the main islands' (open). */
  femaleShare: 0.5,
  /**
   * "Y377 turned 32 years old in 2016, making her, to our knowledge, the
   * oldest wild seal based on tagging data", "at least 32 years" (NOAA
   * Fisheries, "The Grand Dame of Monk Seals", 16 March 2017; a seal of
   * French Frigate Shoals): none lives to 33.
   */
  oldestYears: 32,
  /**
   * "Hawaiian monk seals give birth throughout the year, but peak pupping
   * season is typically spring through summer (March through August)"
   * (NOAA Fisheries' frequently asked questions on monk seal mothers and
   * pups, updated 16 July 2026; "a broad peak in pupping from March to
   * August", NMFS 2014, p. 3-13): every pup born from 1 March to 31 August,
   * evenly, none outside, as no source the game may carry gives the share
   * born outside it or the shape within it. A check, not carried: of the
   * main islands' 237 births in Robinson et al. 2021's Fig. 4, 91.0% fall in
   * those months, their mean day of the year 146.1 against the season's
   * middle, 151.5 (MEASURED, M119, from M103's reading of the chart).
   */
  pupping: { from: [3, 1], to: [8, 31] },
};

/** The pupping season's days in a common year, and its middle day of the year (1 the first of January). */
const COMMON_YEAR_FIRST = epochDayFromCivil(2001, 1, 1);
const seasonDays = (life: SealLife): { first: number; last: number } => ({
  first: epochDayFromCivil(2001, life.pupping.from[0], life.pupping.from[1]) - COMMON_YEAR_FIRST + 1,
  last: epochDayFromCivil(2001, life.pupping.to[0], life.pupping.to[1]) - COMMON_YEAR_FIRST + 1,
});

/** Pups a year to each female of an age (years): none before `firstPupYears`, half the rate at it, the whole rate after. */
export const pupsAt = (life: SealLife, ageYears: number): number =>
  ageYears < life.firstPupYears ? 0 : ageYears === life.firstPupYears ? life.adultPups / 2 : life.adultPups;

/** The classes: 0 to `oldestYears`. */
export const sealClasses = (life: SealLife): number => life.oldestYears + 1;

/** Maui Nui's share of the main islands' seals (its pups'). */
export const mauiNuiShare = (life: SealLife): number => life.mauiNuiPups / life.mainIslandPups;

/** The day of the year pups are born on, on average (1 the first of January): the pupping season's middle, in a common year. */
function birthDay(life: SealLife): number {
  const { first, last } = seasonDays(life);
  return (first + last) / 2;
}

/** The share of a year's births born on a day: an even share of the season's days if the day is in it (its months' days, a leap year's the same), else none. */
function birthShare(life: SealLife, epochDay: number): number {
  const { month, day } = civilFromEpochDay(epochDay);
  const [fm, fd] = life.pupping.from;
  const [tm, td] = life.pupping.to;
  const inSeason = (month > fm || (month === fm && day >= fd)) && (month < tm || (month === tm && day <= td));
  if (!inSeason) return 0;
  const { first, last } = seasonDays(life);
  return 1 / (last - first + 1);
}

/** A weaned seal's chance of dying in a day at its age: weaning to one over the year less its nursing, then each age's year. */
export function dailyDeathAt(life: SealLife, ageYears: number): number {
  if (ageYears < 1) return 1 - life.weanedSurvival ** (1 / (YEAR_DAYS - life.nursingDays));
  // The young's years, 1 to 3, each its own; an adult from the year after them.
  const adultFrom = 1 + life.youngSurvival.length;
  const survival = ageYears < adultFrom ? (life.youngSurvival[Math.floor(ageYears) - 1] as number) : ageYears < life.adultUntilYears ? life.adultSurvival : life.oldSurvival;
  return dailyDeath(survival);
}

/** On the first of January, every class a year older, the oldest's seals dead of age; on any other day, nothing. */
function ageSeals(life: SealLife, s: SealState, epochDay: number): void {
  if (dayOfYear(epochDay) !== 1) return;
  const c = s.counts;
  for (let k = sealClasses(life) - 1; k > 0; k--) c[k] = c[k - 1] as number;
  c[0] = 0;
}

/**
 * A day of the seals' lives, the classes aged (`ageSeals`): the pups whose
 * nursing ends weaned into their birth year's class; the day's pups, the
 * females of each class at its age's rate a year (`pupsAt`), the day's share
 * of the year's births (`pupping`); then deaths, the nursing pups (the
 * day's among them) at the weaning survival spread over the nursing days,
 * every class at its mean age's rate.
 */
export function stepMonkSeals(life: SealLife, s: SealState, epochDay: number, draws: PopulationDraws): void {
  ageSeals(life, s, epochDay);
  const doy = dayOfYear(epochDay);
  const last = life.nursingDays - 1;
  const weaned = s.nursing[last] as number;
  if (weaned > 0) {
    // Born `nursingDays` days ago: this year's class, or last year's across the year's turn.
    const cls = civilFromEpochDay(epochDay).year - civilFromEpochDay(epochDay - life.nursingDays).year;
    s.counts[cls] = (s.counts[cls] as number) + weaned;
  }
  for (let d = last; d > 0; d--) s.nursing[d] = s.nursing[d - 1] as number;
  s.nursing[0] = 0;
  const share = birthShare(life, epochDay);
  if (share > 0) {
    let pups = 0;
    for (let k = 0; k < s.counts.length; k++) pups += (s.counts[k] as number) * pupsAt(life, k);
    s.nursing[0] = draws.count(pups * life.femaleShare * share);
  }
  const nursingDeath = 1 - life.weaningSurvival ** (1 / life.nursingDays);
  for (let d = 0; d <= last; d++) {
    const n = s.nursing[d] as number;
    if (n > 0) s.nursing[d] = n - draws.share(n, nursingDeath);
  }
  // Each class's seals at their mean age on the day (class 0 this year's weaned pups): its years, and the share of a
  // year since the pups' mean birth day.
  const sinceBirth = (doy - birthDay(life)) / YEAR_DAYS;
  for (let k = 0; k < s.counts.length; k++) {
    const n = s.counts[k] as number;
    if (n > 0) s.counts[k] = n - draws.share(n, dailyDeathAt(life, k + sinceBirth));
  }
}

/**
 * One tagged weaned seal's day (M148), as its class's (`stepMonkSeals`'s rates): a year older on the first of January,
 * dead of its age past the oldest class as its class is, then dead by its age's chance or not. `u` its own even draw.
 */
export function stepOneSeal(life: SealLife, seal: { cls: number }, epochDay: number, u: () => number): 'old age' | 'natural' | null {
  const doy = dayOfYear(epochDay);
  if (doy === 1) {
    if (seal.cls >= sealClasses(life) - 1) return 'old age';
    seal.cls += 1;
  }
  return u() < dailyDeathAt(life, seal.cls + (doy - birthDay(life)) / YEAR_DAYS) ? 'natural' : null;
}

/** A weaned seal's age in years on a day, its class's mean (M148). */
export const sealAgeYears = (life: SealLife, cls: number, epochDay: number): number => cls + (dayOfYear(epochDay) - birthDay(life)) / YEAR_DAYS;

/** What the card reads on a day: Maui Nui's seals, this year's pups (weaned and nursing), and those nursing. */
export interface SealCounts {
  population: number;
  pups: number;
  nursing: number;
}
export function sealCounts(life: SealLife, s: SealState, epochDay: number): SealCounts {
  const { year } = civilFromEpochDay(epochDay);
  const nursing = s.nursing.reduce((a, b) => a + b, 0);
  // The nursing born this year: those under the day of the year in age.
  const nursingThisYear = s.nursing.reduce((a, n, d) => a + (civilFromEpochDay(epochDay - d).year === year ? n : 0), 0);
  return { population: s.counts.reduce((a, b) => a + b, 0) + nursing, pups: (s.counts[0] as number) + nursingThisYear, nursing };
}

export function emptySeals(life: SealLife): SealState {
  return { counts: Array<number>(sealClasses(life)).fill(0), nursing: Array<number>(life.nursingDays).fill(0) };
}
